import PropTypes from 'prop-types';
import { Box, Typography, IconButton, Button, Divider } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import StatusPill from 'features/IRWorkstation/components/StatusPill';

const priorityColorMap = {
	High: 'signal.red',
	Normal: 'signal.amber',
	Low: 'signal.green',
};

const TaskField = ({ label, value, color }) => (
	<Box sx={{ mb: 2 }}>
		<Typography
			sx={{
				fontSize: '11px',
				fontWeight: 600,
				textTransform: 'uppercase',
				color: 'text.tertiary',
				mb: '4px',
			}}
		>
			{label}
		</Typography>
		<Typography
			sx={{
				fontSize: '13px',
				fontWeight: 500,
				color: color || 'text.primary',
			}}
		>
			{value || '—'}
		</Typography>
	</Box>
);

const SalesforceTaskDrawer = ({ open, task, onClose }) => {
	return (
		<>
			{/* backdrop */}
			<Box
				onClick={onClose}
				sx={{
					position: 'absolute',
					inset: 0,
					backgroundColor: 'rgba(0,0,0,0.25)',
					opacity: open ? 1 : 0,
					pointerEvents: open ? 'auto' : 'none',
					transition: 'opacity 225ms cubic-bezier(0.4, 0, 0.2, 1)',
					zIndex: 1200,
				}}
			/>

			{/* panel */}
			<Box
				sx={{
					position: 'absolute',
					top: 0,
					right: 0,
					bottom: 0,
					width: 480,
					backgroundColor: 'background.paper',
					transform: open ? 'translateX(0)' : 'translateX(100%)',
					transition: 'transform 225ms cubic-bezier(0, 0, 0.2, 1)',
					zIndex: 1201,
					display: 'flex',
					flexDirection: 'column',
					boxShadow: open ? '-4px 0 20px rgba(0,0,0,0.08)' : 'none',
				}}
			>
				{/* header */}
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'flex-start',
						padding: '20px 24px',
						borderBottom: '1px solid',
						borderColor: 'border.light',
						backgroundColor: 'background.subtle',
					}}
				>
					{!task ? (
						<Box sx={{ py: 2 }}>
							<Typography sx={{ color: 'text.secondary' }}>
								No task selected
							</Typography>
						</Box>
					) : (
						<Box>
							<Box
								sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
							>
								<StatusPill label='Salesforce Task' color='blue' />
								<Typography
									sx={{
										fontSize: '11px',
										color: priorityColorMap[task.priority] || 'text.secondary',
										fontWeight: 700,
										textTransform: 'uppercase',
										marginLeft: '8px',
									}}
								>
									{task.priority} Priority
								</Typography>
							</Box>
							<Typography
								sx={{ fontSize: '18px', fontWeight: 700, lineHeight: 1.3 }}
							>
								{task.subject}
							</Typography>
						</Box>
					)}
					<IconButton
						onClick={onClose}
						sx={{
							mt: -0.5,
							mr: -1,
							color: 'text.tertiary',
							'&:hover': { backgroundColor: 'background.subtle' },
						}}
					>
						<CloseIcon />
					</IconButton>
				</Box>

				{/* body */}
				{task && (
					<Box sx={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
						<Box
							sx={{
								display: 'grid',
								gridTemplateColumns: '1fr 1fr',
								gap: '16px',
								mb: 4,
							}}
						>
							<TaskField label='Status' value={task.status} />
							<TaskField label='Due Date' value={task.due_date} />
							<TaskField
								label='Related To'
								value={task.related_to}
								color='primary.main'
							/>
							<TaskField label='Name' value={task.name} color='primary.main' />
						</Box>

						<Divider sx={{ mb: 3 }} />

						<Box sx={{ mb: 4 }}>
							<Typography
								sx={{
									fontSize: '11px',
									fontWeight: 600,
									textTransform: 'uppercase',
									color: 'text.tertiary',
									mb: '8px',
								}}
							>
								Comments
							</Typography>
							<Box
								sx={{
									backgroundColor: 'background.subtle',
									borderRadius: '8px',
									padding: '16px',
									border: '0.5px solid',
									borderColor: 'border.light',
								}}
							>
								<Typography
									sx={{
										fontSize: '14px',
										lineHeight: 1.6,
										color: 'text.primary',
										whiteSpace: 'pre-wrap',
									}}
								>
									{task.comments || 'No comments provided.'}
								</Typography>
							</Box>
						</Box>

						<Box
							sx={{
								display: 'grid',
								gridTemplateColumns: '1fr 1fr',
								gap: '16px',
							}}
						>
							<TaskField label='Created By' value={task.created_by} />
							<TaskField
								label='Last Modified By'
								value={task.last_modified_by}
							/>
						</Box>
					</Box>
				)}

				{/* actions */}
				{task && (
					<Box
						sx={{
							padding: '16px 24px',
							borderTop: '1px solid',
							borderColor: 'border.light',
							display: 'flex',
							gap: 2,
						}}
					>
						<Button
							variant='outlined'
							fullWidth
							sx={{
								py: 1.2,
								fontWeight: 600,
								borderColor: 'status.green.text',
								color: 'status.green.text',
								'&:hover': {
									backgroundColor: 'status.green.bg',
								},
							}}
						>
							Mark Complete
						</Button>
						<Button
							variant='outlined'
							fullWidth
							sx={{ py: 1.2, fontWeight: 600 }}
						>
							Edit Comments
						</Button>
						<Button
							variant='outlined'
							fullWidth
							sx={{ py: 1.2, fontWeight: 600 }}
						>
							Change Date
						</Button>
					</Box>
				)}
			</Box>
		</>
	);
};

SalesforceTaskDrawer.propTypes = {
	open: PropTypes.bool.isRequired,
	task: PropTypes.object,
	onClose: PropTypes.func.isRequired,
};

export default SalesforceTaskDrawer;
